import { Colors } from "@eai-toolkit-material-ui/theme";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import type { StepperProps, StepProps } from "@mui/material";
import {
  Box,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect } from "react";

import {
  useValidationContextValue,
  ValidationContext,
} from "@components/ValidationContext/ValidationContext";

import { lightenWithRgba } from "../../utils/colorUtils";

const extraSpaceAfterIcon = 6;

const StepRoot = styled(Step, {
  shouldForwardProp: (prop) => !["active"].includes(prop as string),
})<{ active?: boolean }>(({ theme, active }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(2),
  paddingLeft: extraSpaceAfterIcon,
  paddingBottom: theme.spacing(1),
  ...(active
    ? {}
    : {
        "&:hover": {
          cursor: "pointer",
          backgroundColor: Colors.purple[50],
        },
      }),
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  ...theme.typography.h4,
}));

const StepErrorNotification = styled(Tooltip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const StepTitle = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  [`& .${StepErrorNotification}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const StepTitleText = styled("span", {
  shouldForwardProp: (prop) => !["inputRequired"].includes(prop as string),
})<{ inputRequired?: boolean }>(({ inputRequired }) => ({
  ...(inputRequired && {
    "&::after": {
      content: '" *"',
    },
  }),
}));

const StepInfoIcon = styled(InfoOutlined)(({ theme }) => ({
  fontSize: 18,
  marginLeft: theme.spacing(1),
  color: theme.palette.text.secondary,
  cursor: "help",
}));

const StepSummary = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingBottom: theme.spacing(1),
  overflowWrap: "break-word",
  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.spacing(6),
  },
  "& > span": {
    "&:not(:last-child)::after": {
      content: '" | "',
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },
}));

const StyledStepContent = styled(StepContent)(({ theme }) => ({
  paddingTop: 0,
  transition: `padding-top ${theme.transitions.duration.short}ms ${theme.transitions.easing.sharp}`,
  borderLeftColor: "transparent",
  [theme.breakpoints.down("sm")]: {
    paddingLeft: 0,
    marginLeft: 0,
  },
  "&.expanded": {
    paddingTop: theme.spacing(3),
  },
  "& .MuiCollapse-wrapper": {
    visibility: "initial",
    marginLeft: theme.spacing(2),
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  backgroundColor: "transparent",
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: theme.spacing(3),
}));

type FormStepProps = StepProps & {
  label: string;
  index: number;
  onStepSelect: (index: number) => void;
  inputRequired?: boolean;
  overviewItems?: string[];
  tooltip?: string;
  contentPaddingBottom?: number;
};

function FormStep(props: FormStepProps) {
  const {
    label,
    onStepSelect,
    overviewItems,
    tooltip,
    contentPaddingBottom = 0,
    children,
    inputRequired,
    ...rest
  } = props;
  const handleStepSelect = useCallback(
    () => onStepSelect(props.index),
    [onStepSelect, props.index],
  );
  const { validationContextValue, validateAllFieldsInSection } =
    useValidationContextValue();
  useEffect(() => {
    if (props.active) {
      // validate all this step's fields when it's no longer active
      return validateAllFieldsInSection;
    }
  }, [props.active, validateAllFieldsInSection]);
  const hasErrors = [...validationContextValue.errors.values()].some(
    ({ visible }) => visible,
  );
  return (
    <StepRoot
      onClick={handleStepSelect}
      active={props.active}
      data-step-index={props.index}
      {...(hasErrors ? { "data-has-errors": true } : {})}
      {...rest}
    >
      <StyledStepLabel
        StepIconProps={{
          sx: (theme) => ({
            width: theme.spacing(5),
            height: theme.spacing(5),
            color: theme.palette.primary.main,
            position: "relative",
            left: -6,
            "& circle": {
              r: 9,
              stroke: lightenWithRgba(theme.palette.primary.main),
              strokeWidth: 0,
              transition: `stroke-width ${theme.transitions.duration.short}ms ${theme.transitions.easing.sharp}`,
            },
            "&.active circle": {
              strokeWidth: 5,
            },
            fontSize: "0.7rem",
          }),
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          sx={{
            textTransform: "uppercase",
          }}
        >
          Step {props.index + 1}
        </Typography>
        <StepTitle variant="h4">
          <StepTitleText inputRequired={inputRequired}>{label}</StepTitleText>
          {tooltip && (
            <Tooltip title={tooltip} disableInteractive>
              <StepInfoIcon />
            </Tooltip>
          )}
          {hasErrors && (
            <StepErrorNotification
              placement="right"
              title="Some errors need to be fixed in this step"
            >
              <ErrorOutline color="error" />
            </StepErrorNotification>
          )}
        </StepTitle>
      </StyledStepLabel>
      {overviewItems && (
        <StepSummary>
          {overviewItems.map((text, index) => (
            <span key={index}>{text}</span>
          ))}
        </StepSummary>
      )}
      <StyledStepContent
        onFocus={handleStepSelect}
        className={props.active ? "expanded" : ""}
        TransitionProps={{ unmountOnExit: false }}
      >
        <ValidationContext.Provider value={validationContextValue}>
          <Box pb={contentPaddingBottom}>{children}</Box>
        </ValidationContext.Provider>
      </StyledStepContent>
    </StepRoot>
  );
}

type FormStepperProps = Omit<StepperProps, "children"> & {
  onStepSelect: (index: number) => void;
  activeIndex: number;
  /** Default trailing padding inside each step; can be overridden per-step. */
  contentPaddingBottom?: number;
  children: {
    label: string;
    children: React.ReactNode;
    inputRequired?: boolean;
    overviewItems?: string[];
    tooltip?: string;
    contentPaddingBottom?: number;
  }[];
};

function FormStepper({
  onStepSelect,
  activeIndex,
  contentPaddingBottom: defaultContentPaddingBottom,
  children,
  ...rest
}: FormStepperProps) {
  return (
    <StyledStepper orientation="vertical" connector={<></>} {...rest}>
      {children.map(
        (
          {
            label,
            inputRequired,
            overviewItems,
            tooltip,
            contentPaddingBottom,
            children,
          },
          index,
        ) =>
          React.createElement(
            FormStep,
            {
              index,
              key: label,
              label,
              onStepSelect,
              inputRequired,
              overviewItems,
              tooltip,
              contentPaddingBottom:
                contentPaddingBottom ?? defaultContentPaddingBottom,
              active: index === activeIndex,
            },
            children,
          ),
      )}
    </StyledStepper>
  );
}

export default FormStepper;
